import { AnchorTreeNode } from '../types/anchor-tree';

/**
 * Immutable editing helpers for the anchor tree draft: every mutation returns
 * a new root with the path to the edited node copied (untouched subtrees keep
 * their references), matching the page-level `draftRoot` signal + JSON-compare
 * `isDirty` model.
 */

/** New empty node of the given kind (same shapes the authoring page creates). */
export function createNode(kind: 'decision' | 'leaf'): AnchorTreeNode {
  return kind === 'decision'
    ? { id: crypto.randomUUID(), kind: 'decision', decisiveAnchorFieldId: '', branches: [] }
    : { id: crypto.randomUUID(), kind: 'leaf', leafAnchorFieldIds: [], templateIds: [] };
}

/** Finds a node by id anywhere in the tree. */
export function findNode(root: AnchorTreeNode | null, nodeId: string): AnchorTreeNode | null {
  if (!root) {
    return null;
  }
  if (root.id === nodeId) {
    return root;
  }
  if (root.kind === 'decision') {
    for (const branch of root.branches ?? []) {
      const found = findNode(branch.child, nodeId);
      if (found) {
        return found;
      }
    }
  }
  return null;
}

/**
 * Replaces the node with `replacement.id` by `replacement`, path-copying its
 * ancestors. Returns the original root when the id is not in the tree.
 */
export function replaceNode(root: AnchorTreeNode | null, replacement: AnchorTreeNode): AnchorTreeNode | null {
  if (!root) {
    return null;
  }
  if (root.id === replacement.id) {
    return replacement;
  }
  if (root.kind !== 'decision') {
    return root;
  }
  let changed = false;
  const branches = (root.branches ?? []).map(branch => {
    const child = replaceNode(branch.child, replacement);
    if (child === branch.child) {
      return branch;
    }
    changed = true;
    return { ...branch, child: child! };
  });
  return changed ? { ...root, branches } : root;
}

/**
 * Removes the node (with the branch leading to it) and its whole subtree.
 * Removing the root returns null.
 */
export function removeNode(root: AnchorTreeNode | null, nodeId: string): AnchorTreeNode | null {
  if (!root) {
    return null;
  }
  if (root.id === nodeId) {
    return null;
  }
  if (root.kind !== 'decision') {
    return root;
  }
  let changed = false;
  const branches = (root.branches ?? [])
    .filter(branch => {
      if (branch.child.id === nodeId) {
        changed = true;
        return false;
      }
      return true;
    })
    .map(branch => {
      const child = removeNode(branch.child, nodeId);
      if (child === branch.child) {
        return branch;
      }
      changed = true;
      return { ...branch, child: child! };
    });
  return changed ? { ...root, branches } : root;
}

/**
 * Appends a new empty-valued branch with a fresh child node to the decision
 * node. Returns the original root (and a null id) when the target is missing
 * or not a decision node.
 */
export function addBranch(
  root: AnchorTreeNode | null,
  decisionNodeId: string,
  kind: 'decision' | 'leaf'
): { root: AnchorTreeNode | null; newChildId: string | null } {
  const target = findNode(root, decisionNodeId);
  if (!target || target.kind !== 'decision') {
    return { root, newChildId: null };
  }
  const child = createNode(kind);
  const updated = {
    ...target,
    branches: [...(target.branches ?? []), { expectedValue: '', child }]
  };
  return { root: replaceNode(root, updated), newChildId: child.id };
}

/** Sets the expected value of the decision node's `branchIndex`-th branch. */
export function updateBranchValue(
  root: AnchorTreeNode | null,
  decisionNodeId: string,
  branchIndex: number,
  expectedValue: string
): AnchorTreeNode | null {
  const target = findNode(root, decisionNodeId);
  if (!target || target.kind !== 'decision' || !target.branches?.[branchIndex]) {
    return root;
  }
  const branches = target.branches.map((b, i) => (i === branchIndex ? { ...b, expectedValue } : b));
  return replaceNode(root, { ...target, branches });
}

/** Total templates placed on leaves of the given subtree. */
export function countTemplatesInSubtree(node: AnchorTreeNode | null): number {
  if (!node) {
    return 0;
  }
  if (node.kind === 'leaf') {
    return node.templateIds?.length ?? 0;
  }
  return (node.branches ?? []).reduce((acc, branch) => acc + countTemplatesInSubtree(branch.child), 0);
}
